<script>
/**
 * TaskAssigneePicker — inline assignee selector with integrated avatar trigger.
 *
 * Renders an avatar stack (when filled) or a muted placeholder (when empty)
 * as the activator. Clicking opens a popover with a simple list of assignees,
 * each showing avatar + name + a check icon when selected.
 *
 * Supports both professionals and clients (backend returns them as two
 * separate lists). Sections render only when non-empty.
 *
 * Props:
 *  - value: Array of selected assignees [{ id, tipo, nome, avatarUrl }]
 *  - mode: 'single' | 'multi' (default 'single') — single closes on pick
 *  - label: optional label rendered above the trigger
 *  - placeholder: text shown when no one is selected
 *  - markResponsavel: id to flag as "Responsável" inside the list
 *  - maxVisible: avatar-stack overflow threshold (default 3)
 *  - disabled: disables the trigger
 *  - excludePerson: { id, tipo } | null — hides this person from the dropdown
 *    menu. Used by the detail form to keep the "responsavelPrincipal" out of
 *    the "participantes" picker (backend mutex is authoritative; this is UX).
 *
 * Emits:
 *  - input: Array<{ id, tipo, nome, avatarUrl }>
 *  - menu: Boolean — abertura/fechamento do popover
 */
import {
  personName,
  personAvatar,
  personBgColor,
  personTextColor,
  personInitials,
} from '@modules/tarefas/utils/personDecoration'

function normalize(str) {
  if (!str) return ''
  return String(str)
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()
}

// Renderização incremental das listas do dropdown. Mesmo tamanho de página do
// `select-search` (`itemsPerLoad: 50`), pela mesma razão: um escritório tem
// centenas de profissionais e milhares de clientes, e renderizar tudo na abertura
// era o custo dominante do menu (medido: 885 opções, 3.558 nós de DOM).
const LIST_PAGE_SIZE = 50
// Distância (px) do fim da lista em que a janela cresce.
const LIST_GROW_THRESHOLD = 120
// Intervalo mínimo entre dois crescimentos — ver a nota em `onListScroll`.
const LIST_GROW_MIN_INTERVAL_MS = 150

export default {
  name: 'TaskAssigneePicker',
  props: {
    mode: {
      type: String,
      default: 'single',
      validator: function (v) {
        return v === 'single' || v === 'multi'
      },
    },
    value: {
      type: Array,
      default: function () {
        return []
      },
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Selecionar responsável',
    },
    markResponsavel: {
      type: [Number, String],
      default: null,
    },
    maxVisible: {
      type: Number,
      default: 3,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    excludePerson: {
      type: Object,
      default: null,
    },
    /**
     * Quando true, o estado vazio mostra apenas o ícone — sem o texto do
     * `placeholder`. Usado nas linhas de tarefa/subtarefa, onde o header de
     * colunas já rotula a célula e o texto repetido só polui a linha. O
     * `placeholder` continua acessível via `aria-label`/`title` do trigger.
     */
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuOpen: false,
      search: '',
      // Uma janela de render POR SEÇÃO. Independentes porque as seções têm tamanhos
      // muito diferentes (centenas de profissionais, milhares de clientes) e uma
      // janela global faria os clientes só aparecerem depois de revelar todos os
      // profissionais.
      visibleProfessionalsCount: LIST_PAGE_SIZE,
      visibleClientsCount: LIST_PAGE_SIZE,
    }
  },
  computed: {
    // Listas JÁ decoradas, compartilhadas por todos os pickers da aplicação (ver
    // `assigneesDecoratedProfessionals` em store/getters.js). Os filtros abaixo
    // devolvem SUBCONJUNTOS destas referências — nunca cópias — para que abrir um
    // picker não materialize a lista do escritório de novo.
    //
    // A variante `...SelfFirst` hoista o usuário logado para o topo sem quebrar
    // esse contrato: só o array externo é novo, os itens são as mesmas
    // referências. O hoist acontece ANTES de `excludePerson` e da busca, mas
    // ambos são `.filter()` — que preserva ordem — então o resultado observável
    // é o mesmo de hoistar depois: se o logado não casa a busca ou está
    // excluído, ele simplesmente não aparece.
    professionals: function () {
      return this.$store.getters['tarefas/assigneesDecoratedProfessionalsSelfFirst']
    },
    clients: function () {
      return this.$store.getters['tarefas/assigneesDecoratedClients']
    },
    loading: function () {
      return this.$store.getters['tarefas/assigneesLoading']
    },
    selectedKeys: function () {
      const ids = {}
      for (const entry of this.value) {
        // Backend responses sometimes omit `tipo` on persisted assignees;
        // track a tipo-agnostic key so the dropdown still highlights the
        // current selection even when tipo is missing.
        ids[entry.id + '_' + (entry.tipo || '')] = true
        ids[entry.id + '_*'] = true
      }
      return ids
    },
    filteredProfessionals: function () {
      let base = this.professionals
      if (this.excludePerson && this.excludePerson.tipo === 'profissional') {
        const excludedId = this.excludePerson.id
        base = base.filter(function (p) {
          return p.id !== excludedId
        })
      }
      if (!this.search || !this.search.length) return base
      const q = normalize(this.search)
      return base.filter(function (p) {
        return normalize(p.nome).includes(q)
      })
    },
    filteredClients: function () {
      let base = this.clients
      if (this.excludePerson && this.excludePerson.tipo === 'cliente') {
        const excludedId = this.excludePerson.id
        base = base.filter(function (c) {
          return c.id !== excludedId
        })
      }
      if (!this.search || !this.search.length) return base
      const q = normalize(this.search)
      return base.filter(function (c) {
        return normalize(c.nome).includes(q)
      })
    },
    hasResults: function () {
      return this.filteredProfessionals.length > 0 || this.filteredClients.length > 0
    },
    // Janelas renderizadas. Os `filtered*` seguem sendo o conjunto-verdade
    // (cabeçalhos de seção, empty-state, teto do crescimento).
    visibleProfessionals: function () {
      return this.filteredProfessionals.slice(0, this.visibleProfessionalsCount)
    },
    visibleClients: function () {
      return this.filteredClients.slice(0, this.visibleClientsCount)
    },
    visibleValue: function () {
      if (!this.value || !this.value.length) return []
      return this.value.slice(0, this.maxVisible)
    },
    overflowCount: function () {
      if (!this.value) return 0
      return Math.max(0, this.value.length - this.maxVisible)
    },
    // Decorated view-models computed from the input lists (never from stale
    // reactive state). Each item carries every value the template needs
    // (avatar, initials, colors, selection/marked flags) so the template has
    // zero function calls in interpolations / v-if / :style bindings (AC-M4).
    decoratedVisibleValue: function () {
      const total = this.visibleValue.length
      return this.visibleValue.map((person, idx) => {
        const avatar = personAvatar(person)
        const nome = personName(person)
        return {
          key: person.id + '_' + (person.tipo || '') + '_' + idx,
          nome: nome,
          initials: personInitials(nome),
          avatar: avatar,
          bgColor: avatar ? null : personBgColor(person),
          textColor: avatar ? null : personTextColor(person),
          marginLeft: idx > 0 ? '-6px' : '0',
          zIndex: total - idx,
        }
      })
    },
    // Flag de seleção por id, para o template ler `selectedById[prof.id]` — acesso
    // a propriedade, sem chamada de função e sem recriar os itens da lista.
    //
    // Indexa por ID, não por `id_tipo`, porque é exatamente o que o
    // `isSelected` anterior fazia: ele marcava `id + '_*'` para TODA entrada e
    // aceitava esse fallback, então qualquer entrada com aquele id já contava como
    // selecionada, independente do tipo. Respostas do backend às vezes omitem
    // `tipo` em responsáveis persistidos, e é isso que mantém o destaque correto.
    selectedById: function () {
      const ids = {}
      for (const entry of this.value) ids[entry.id] = true
      return ids
    },
  },
  watch: {
    menuOpen: function (newVal) {
      if (!newVal) this.search = ''
      // Reabrir sempre começa limitado, senão a janela inflada pelo scroll da
      // abertura anterior é herdada e o menu renderiza tudo de uma vez no clique.
      this.resetWindows()
      // Quem hospeda o picker precisa saber que um popover está aberto — a
      // linha de criação inline usa isso para não commitar a tarefa quando o
      // input de título perde o foco por causa deste menu
      // (`task-inline-create-row`). Consumidor que não escuta ignora de graça.
      this.$emit('menu', newVal)
    },
    // Busca nova = conjunto novo: as janelas voltam ao início, senão um filtro que
    // devolve poucos itens herdaria uma janela grande sem necessidade.
    search: function () {
      this.resetWindows()
    },
  },
  created: function () {
    // Timestamp do último crescimento. Fora do `data` de propósito: é controle de
    // throttle, não estado de render — não deve disparar reatividade.
    this._lastGrowAt = 0
  },
  methods: {
    resetWindows: function () {
      this.visibleProfessionalsCount = LIST_PAGE_SIZE
      this.visibleClientsCount = LIST_PAGE_SIZE
    },
    // Cresce ao chegar perto do fim da lista.
    //
    // Cresce a ÚLTIMA seção com itens ocultos — Clientes antes de Profissionais.
    // A ordem não é arbitrária: Clientes é a seção de baixo, então revelar mais
    // itens ali apenas acrescenta conteúdo abaixo do que está em vista. Crescer
    // Profissionais insere linhas ACIMA e desloca o que o usuário está olhando, o
    // que só acontece depois de a carteira inteira já estar revelada.
    //
    // O throttle não é decoração: um único gesto de scroll dispara dezenas de
    // eventos, e sem ele cada um somaria uma página antes do re-render — foi
    // exatamente o defeito corrigido em 967848d9a na Lista, onde rolar até o fim
    // levava 30 linhas a 750.
    onListScroll: function (event) {
      const el = event && event.target
      if (!el) return

      const clientsHidden = this.visibleClientsCount < this.filteredClients.length
      const profsHidden =
        this.visibleProfessionalsCount < this.filteredProfessionals.length
      if (!clientsHidden && !profsHidden) return

      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
      if (remaining > LIST_GROW_THRESHOLD) return

      const now = Date.now()
      if (now - this._lastGrowAt < LIST_GROW_MIN_INTERVAL_MS) return
      this._lastGrowAt = now

      if (clientsHidden) {
        this.visibleClientsCount = Math.min(
          this.visibleClientsCount + LIST_PAGE_SIZE,
          this.filteredClients.length
        )
        return
      }
      this.visibleProfessionalsCount = Math.min(
        this.visibleProfessionalsCount + LIST_PAGE_SIZE,
        this.filteredProfessionals.length
      )
    },
    togglePerson(person, tipo) {
      const entry = {
        id: person.id,
        tipo: tipo,
        nome: person.nome,
        avatarUrl: person.avatar_url || person.avatarUrl || null,
      }

      if (this.mode === 'single') {
        this.$emit('input', [entry])
        this.menuOpen = false
        return
      }

      const key = person.id + '_' + tipo
      if (this.selectedKeys[key]) {
        const filtered = this.value.filter(function (v) {
          return (v.id + '_' + v.tipo) !== key
        })
        this.$emit('input', filtered)
      } else {
        this.$emit('input', this.value.concat([entry]))
      }
    },
  },
}
</script>

<template>
  <div class="task-assignee-picker">
    <span v-if="label" class="az-text-small text-color-600 mb-1 d-block">{{ label }}</span>

    <!-- Sem `max-height` aqui de propósito: se o v-menu for o scroller, o campo de
         busca rola junto e sai de vista. A altura passa a ser governada pelo
         `__list`, o único elemento com overflow. `max-width` porque sem ele o
         conteúdo empurrava o menu para 465px. -->
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      offset-y
      min-width="280"
      max-width="300"
      transition="scale-transition"
      content-class="task-assignee-picker__menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="task-assignee-picker__trigger d-inline-flex align-center"
          :class="{ 'task-assignee-picker__trigger--empty': !value.length }"
          :disabled="disabled"
          :aria-label="value.length ? 'Alterar responsável' : 'Adicionar responsável'"
          :title="value.length ? null : placeholder"
          v-bind="attrs"
          v-on="on"
        >
          <template v-if="!value.length">
            <icon-svg name="mdi-account-plus" :size="16" color="var(--secondary)" />
            <span v-if="!iconOnly" class="az-text-small text-color-400 ml-1">{{ placeholder }}</span>
          </template>
          <template v-else>
            <span
              v-for="person in decoratedVisibleValue"
              :key="person.key"
              class="task-assignee-picker__avatar"
              :style="{
                marginLeft: person.marginLeft,
                zIndex: person.zIndex,
                backgroundColor: person.bgColor,
                color: person.textColor,
              }"
              :title="person.nome"
            >
              <img
                v-if="person.avatar"
                :src="person.avatar"
                :alt="person.initials"
                class="task-assignee-picker__avatar-img"
              />
              <span v-else>{{ person.initials }}</span>
            </span>
            <span
              v-if="overflowCount > 0"
              class="task-assignee-picker__avatar task-assignee-picker__avatar--overflow"
              :style="{ marginLeft: '-6px' }"
            >
              +{{ overflowCount }}
            </span>
          </template>
        </button>
      </template>

      <!-- O gate `menuOpen` do bloco abaixo é LOAD-BEARING para memória, não
           estética. O conteúdo do v-menu é slot default NÃO-scoped, e o Vue 2
           avalia slot não-scoped no render do PAI — independente de o v-menu
           descartar o resultado enquanto fechado (`showLazyContent` devolve
           undefined). Sem este gate, `decoratedProfessionals` +
           `decoratedClients` materializam a lista inteira do escritório
           (197 + 689 = 886 objetos) em CADA picker de CADA linha, e criam VNodes
           que nunca chegam ao DOM. Com 2 pickers por linha isso é o multiplicador
           que levava "Minhas Tarefas" a GBs de heap.
           Medido: `dirty === false` nos 90 pickers da página sem nenhum menu
           aberto, com apenas 5.739 nós de DOM (as listas não estavam
           renderizadas). -->
      <div v-if="menuOpen" class="task-assignee-picker__dropdown pa-2">
        <div class="px-2 py-1 az-text-small font-weight-bold text-color-500">Atribuir a</div>

        <div class="mb-2 px-1">
          <InputByType
            v-model="search"
            type="search"
            label=""
            placeholder="Buscar..."
            outlined
            dense
            hide-details
            class="new-ux"
          />
        </div>

        <v-progress-linear v-if="loading" indeterminate height="2" class="mb-2" />

        <div v-if="!loading && !hasResults" class="pa-3 text-center">
          <span class="az-text-small text-color-400">Nenhum resultado encontrado</span>
        </div>

        <!-- O ÚNICO scroller do dropdown. O título e a busca ficam fixos acima.
             As duas seções renderizam janelas (`visibleProfessionals` /
             `visibleClients`), não as listas inteiras do escritório. -->
        <div class="task-assignee-picker__list" @scroll="onListScroll">
        <div v-if="filteredProfessionals.length > 0" class="task-assignee-picker__section">
          <div
            v-if="filteredClients.length > 0"
            class="px-2 pt-1 pb-1 az-text-small text-color-500"
          >
            Profissionais
          </div>
          <div
            v-for="prof in visibleProfessionals"
            :key="'prof-' + prof.id"
            class="task-assignee-picker__option d-flex align-center"
            :class="{
              'task-assignee-picker__option--selected': selectedById[prof.id],
            }"
            @click="togglePerson(prof.raw, 'profissional')"
          >
            <span
              class="task-assignee-picker__avatar mr-2"
              :style="{ backgroundColor: prof.bgColor, color: prof.textColor }"
            >
              <img
                v-if="prof.avatar"
                :src="prof.avatar"
                :alt="prof.initials"
                class="task-assignee-picker__avatar-img"
              />
              <span v-else>{{ prof.initials }}</span>
            </span>
            <span class="az-text-small flex-grow-1 text-color-800 text-truncate">
              {{ prof.nome }}
            </span>
            <v-chip
              v-if="prof.id === markResponsavel"
              x-small
              color="primary"
              text-color="white"
              class="ml-1 mr-1"
            >
              Responsável
            </v-chip>
            <icon-svg
              v-if="selectedById[prof.id]"
              name="mdi-check"
              :size="16"
              color="var(--primary)"
            />
          </div>
        </div>

        <div
          v-if="filteredClients.length > 0"
          class="task-assignee-picker__section"
          :class="{ 'mt-1': filteredProfessionals.length > 0 }"
        >
          <div
            v-if="filteredProfessionals.length > 0"
            class="px-2 pt-1 pb-1 az-text-small text-color-500"
          >
            Clientes
          </div>
          <div
            v-for="client in visibleClients"
            :key="'client-' + client.id"
            class="task-assignee-picker__option d-flex align-center"
            :class="{
              'task-assignee-picker__option--selected': selectedById[client.id],
            }"
            @click="togglePerson(client.raw, 'cliente')"
          >
            <span
              class="task-assignee-picker__avatar mr-2"
              :style="{ backgroundColor: client.bgColor, color: client.textColor }"
            >
              <img
                v-if="client.avatar"
                :src="client.avatar"
                :alt="client.initials"
                class="task-assignee-picker__avatar-img"
              />
              <span v-else>{{ client.initials }}</span>
            </span>
            <span class="az-text-small flex-grow-1 text-color-800 text-truncate">
              {{ client.nome }}
            </span>
            <v-chip
              v-if="client.id === markResponsavel"
              x-small
              color="primary"
              text-color="white"
              class="ml-1 mr-1"
            >
              Responsável
            </v-chip>
            <icon-svg
              v-if="selectedById[client.id]"
              name="mdi-check"
              :size="16"
              color="var(--primary)"
            />
          </div>
        </div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style scoped lang="sass">
.task-assignee-picker
  display: inline-block

.task-assignee-picker__trigger
  background: transparent
  border: none
  cursor: pointer
  padding: 4px 8px
  border-radius: 4px
  transition: background-color 0.15s ease
  min-height: 32px

  &:hover:not(:disabled)
    background-color: rgba(0, 0, 0, 0.04)

  &:disabled
    opacity: 0.6
    cursor: not-allowed

.task-assignee-picker__trigger--empty
  padding-left: 4px

.task-assignee-picker__avatar
  display: inline-flex
  align-items: center
  justify-content: center
  width: 22px
  height: 22px
  border-radius: 50%
  background-color: var(--primary)
  color: var(--white)
  font-size: 10px
  font-weight: 600
  border: 2px solid var(--white)
  overflow: hidden
  flex-shrink: 0

.task-assignee-picker__avatar-img
  width: 100%
  height: 100%
  object-fit: cover

.task-assignee-picker__avatar--overflow
  background-color: var(--secondary)
  font-size: 11px

.task-assignee-picker__dropdown
  background-color: var(--white)
  min-width: 280px
  // Sem `overflow`/`max-height` aqui: era isto que fazia o campo de busca rolar
  // junto e sair de vista. O scroller é o `__list` abaixo, e o título e a busca
  // ficam fixos.
  display: flex
  flex-direction: column

.task-assignee-picker__list
  overflow-y: auto
  max-height: 250px
  // `contain: layout paint` isola o recálculo ao container enquanto as janelas
  // crescem, em vez de refazer layout/paint da página a cada passo.
  contain: layout paint

.task-assignee-picker__option
  padding: 6px 8px
  cursor: pointer
  border-radius: 4px
  transition: background-color 0.15s ease

  &:hover
    background-color: rgba(0, 0, 0, 0.04)

.task-assignee-picker__option--selected
  background-color: rgba(86, 175, 121, 0.08)

.task-assignee-picker__option .task-assignee-picker__avatar
  width: 24px
  height: 24px
  font-size: 10px
  border: none
</style>
